import { useState } from 'react';
import { api } from '@/api/client';
import { X, User, Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';

export default function EditProfileModal({ user, onClose, onSaved }) {
  useBodyScrollLock();
  const [nameInput, setNameInput] = useState(user?.display_name || user?.full_name || '');
  const [bioInput, setBioInput] = useState(user?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const { file_url } = await api.storage.uploadAvatar({ file });
      setAvatarUrl(file_url);
    } catch (err) {
      console.error('Avatar upload failed', err);
      setError(err?.message || 'Could not upload that image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.auth.updateMe({ display_name: nameInput, bio: bioInput, avatar_url: avatarUrl });
      // Restamp the cached reviewer_name on this user's past reviews. A single
      // UPDATE behind an RPC, where it used to be a fetch-all plus updateMany.
      await api.functions.invoke('renameMyReviews', { name: nameInput });
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save the profile', err);
      setError(err?.message || 'Could not save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-md p-6 space-y-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg">Edit Profile</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col items-center gap-2">
            <label className="relative w-20 h-20 rounded-full cursor-pointer group overflow-hidden border-2 border-primary/30 flex items-center justify-center bg-secondary">
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-muted-foreground" strokeWidth={1.5} />
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {uploading ? <Loader2 size={18} className="text-white animate-spin" /> : <Camera size={18} className="text-white" />}
              </div>
              <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" disabled={uploading} />
            </label>
            <span className="text-xs text-muted-foreground">Tap to change your icon</span>
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Your name"
              className="rounded-xl"
              maxLength={50}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Bio</Label>
            <p className="text-xs text-muted-foreground">Shown on your public profile.</p>
            <Textarea
              value={bioInput}
              onChange={(e) => setBioInput(e.target.value)}
              placeholder="A short bio about you..."
              className="rounded-xl resize-none"
              rows={3}
              maxLength={120}
            />
            <p className="text-xs text-muted-foreground text-right">{bioInput.length}/120</p>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || uploading || !nameInput.trim()} className="flex-1 rounded-xl">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}